import { succeed } from '@/src/utilities/result';
import {
  loadAlpineAsync,
  loadGoogleMapsAsync,
} from '@/src/utilities/script-loader';

import {
  geocodePostalCode,
  getMapDirectionsOrigin,
  resetMap,
  searchLocations,
} from './service-locator-utilities';

/** @type {import('./service-locator-utilities').MapState} */
const mapState = {
  map: undefined,
  markers: [],
};

export async function load() {
  if (!document.querySelector('[data-app="service-locator"]')) {
    return;
  }

  const { defaultLatitude, defaultLongitude, mapsAPIKey, context, content } =
    /** @type {GlobalState} */ (window.serviceLocator);

  const google = await loadGoogleMapsAsync(mapsAPIKey);

  const el = document.querySelector('[data-google-map]');

  if (!el) {
    return;
  }

  mapState.map = new google.maps.Map(el, {
    zoom: 11,
    center: { lat: defaultLatitude, lng: defaultLongitude },
  });

  const postalCode = context.postalCode || '';

  const Alpine = await loadAlpineAsync();

  Alpine.store('serviceLocator', {
    locations: [],
    searchRadius: '10',
    searchPostalCode: postalCode,
    directionsOrigin: postalCode,
    defaultLatitude,
    defaultLongitude,
    loadingState: 'LOADING',
    context,
    content,

    init,
    showLoading,
    showNoResults,
    onSearchSubmit,
    search,
    directionsURL,
    getGoogle() {
      return google;
    },
  });

  Alpine.start();

  Alpine.store('serviceLocator').init();
}

/**
 * @description Initializes the store from the 'postalcode' querystring param
 *  and initiates the default search
 * @this {ServiceLocatorState}
 */
async function init() {
  this.directionsOrigin = await getMapDirectionsOrigin();

  const google = this.getGoogle();

  const { defaultLatitude, defaultLongitude } = window.serviceLocator;

  const result = this.searchPostalCode
    ? await geocodePostalCode(google, this.searchPostalCode)
    : succeed(
        new google.maps.LatLng({
          lat: defaultLatitude,
          lng: defaultLongitude,
        }),
      );

  if (result.type === 'failure') {
    this.loadingState = 'LOADED';

    return;
  }

  if (result) {
    this.search(result.value);
  }
}

/** @this {ServiceLocatorState} */
function showLoading() {
  return this.loadingState === 'LOADING';
}

/** @this {ServiceLocatorState} */
function showNoResults() {
  return this.loadingState === 'LOADED' && !this.locations.length;
}

/**
 * @description Handles the search form submission, geocoding the postal code
 *  and making a search request with the result
 * @this {ServiceLocatorState}
 */
async function onSearchSubmit() {
  const google = this.getGoogle();

  this.locations = [];
  this.loadingState = 'LOADING';

  const result = await geocodePostalCode(google, this.searchPostalCode);

  if (result.type === 'failure') {
    this.loadingState = 'LOADED';

    return;
  }

  this.search(result.value);
}

/**
 * @description Updates the google map with
 * @this {ServiceLocatorState}
 *  @param {google.maps.LatLng} latLng
 */
async function search(latLng) {
  const google = this.getGoogle();

  resetMap(google, mapState, latLng);

  const result = await searchLocations(
    latLng,
    Number.parseInt(this.searchRadius || '10', 10),
    this.context.culture,
    this.content.miles,
  );

  if (result.type === 'failure') {
    console.error(result.error);

    this.loadingState = 'LOADED';

    return;
  }

  const locations = result.value;

  for (const location of locations) {
    mapState.markers.push(
      new google.maps.Marker({
        map: mapState.map,
        position: new google.maps.LatLng({
          lat: location.coordLat,
          lng: location.coordLong,
        }),
      }),
    );
  }

  this.locations = locations;

  this.loadingState = 'LOADED';
}

/**
 * See: https://developers.google.com/maps/documentation/urls/get-started#directions-action
 * See: https://developer.mozilla.org/en-US/docs/Web/API/Geolocation_API
 * @this {ServiceLocatorState}
 * @param {import('./service-locator-utilities').ServiceLocation} location
 */
function directionsURL(location) {
  const destination = encodeURI(
    `${location.street} ${location.city}, ${location.stateCode}`,
  );

  const origin = encodeURI(
    this.directionsOrigin ||
      this.searchPostalCode ||
      `${this.defaultLatitude},${this.defaultLongitude}`,
  );

  return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
}

/**
 * @typedef GlobalState
 * @property {string} mapsAPIKey
 * @property {number} defaultLatitude
 * @property {number} defaultLongitude
 * @property {{ culture: string, postalCode: string? }} context
 * @property {{ miles: string }} content
 */

/**
 * @typedef ServiceLocatorState
 * @property {import('./service-locator-utilities').ServiceLocation[]} locations
 * @property {string} searchRadius
 * @property {string} searchPostalCode
 * @property {string} directionsOrigin
 * @property {number} defaultLatitude
 * @property {number} defaultLongitude
 * @property {'LOADING' | 'LOADED'} loadingState
 * @property {{ culture: string }} context
 * @property {{ miles: string }} content
 * @property {(latLng: google.maps.LatLng) => Promise<any>} search
 * @property {() => google} getGoogle
 */
